import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/authcontext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from '../api/axios';
import { onGlobalSocketReady } from '../components/GlobalIncomingCallHandler';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

const Messages = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';

  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const [showNewModal, setShowNewModal] = useState(false);
  const [eligiblePractitioners, setEligiblePractitioners] = useState([]);
  const [startingConversation, setStartingConversation] = useState(false);

  const messagesEndRef = useRef(null);
  const selectedIdRef = useRef(null);
  selectedIdRef.current = selectedId;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = useCallback(async () => {
    try {
      setLoadingConversations(true);
      const { data } = await axios.get('/messages/conversations');
      setConversations(Array.isArray(data.conversations) ? data.conversations : []);
    } catch (error) {
      toast.error('Could not load your conversations');
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Real-time incoming messages arrive over the SAME socket
  // GlobalIncomingCallHandler already keeps open for calls, not a second
  // connection of our own — see the note in GlobalIncomingCallHandler.jsx on
  // why two connections for one user would break call routing.
  useEffect(() => {
    const unsubscribe = onGlobalSocketReady((socket) => {
      const handleIncoming = (message) => {
        if (message.conversation_id === selectedIdRef.current) {
          setMessages(prev => [...prev, message]);
          // Already viewing this thread — mark it read immediately rather
          // than leaving an unread badge on a conversation that's open.
          axios.patch(`/messages/conversations/${message.conversation_id}/read`).catch(() => {});
        }

        setConversations(prev => {
          const idx = prev.findIndex(c => c.id === message.conversation_id);
          if (idx === -1) {
            // A conversation that didn't exist in this list yet (e.g. a
            // practitioner just replying for the first time) — simplest
            // correct thing is to refetch rather than guess its shape.
            fetchConversations();
            return prev;
          }
          const updated = [...prev];
          const isOpen = message.conversation_id === selectedIdRef.current;
          updated[idx] = {
            ...updated[idx],
            lastMessage: message.message_text,
            last_message_at: message.created_at,
            unreadCount: isOpen ? 0 : (updated[idx].unreadCount || 0) + 1
          };
          // Bump to top, matching "most recent conversation first".
          const [moved] = updated.splice(idx, 1);
          return [moved, ...updated];
        });
      };

      socket.on('receive-direct-message', handleIncoming);
      return () => socket.off('receive-direct-message', handleIncoming);
    });

    return unsubscribe;
  }, [fetchConversations]);

  const openConversation = async (conversation) => {
    setSelectedId(conversation.id);
    setLoadingMessages(true);
    setMessages([]);

    try {
      const { data } = await axios.get(`/messages/conversations/${conversation.id}/messages`);
      setMessages(Array.isArray(data.messages) ? data.messages : []);

      if (conversation.unreadCount > 0) {
        await axios.patch(`/messages/conversations/${conversation.id}/read`);
        setConversations(prev => prev.map(c => c.id === conversation.id ? { ...c, unreadCount: 0 } : c));
      }
    } catch (error) {
      toast.error('Could not load this conversation');
    } finally {
      setLoadingMessages(false);
      setTimeout(scrollToBottom, 50);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending || !selectedId) return;

    setSending(true);
    const outgoingText = text.trim();
    setText('');

    try {
      // REST is the primary send path here — it's simpler than correlating a
      // socket ack, and delivery to the other person is real-time either way,
      // since the server calls emitToUser() from inside this same endpoint.
      const { data } = await axios.post(`/messages/conversations/${selectedId}/messages`, {
        text: outgoingText
      });
      setMessages(prev => [...prev, data.message]);
      setConversations(prev => {
        const idx = prev.findIndex(c => c.id === selectedId);
        if (idx === -1) return prev;
        const updated = [...prev];
        updated[idx] = { ...updated[idx], lastMessage: data.message.message_text, last_message_at: data.message.created_at };
        const [moved] = updated.splice(idx, 1);
        return [moved, ...updated];
      });
      setTimeout(scrollToBottom, 50);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not send message');
      setText(outgoingText); // give it back so nothing is silently lost
    } finally {
      setSending(false);
    }
  };

  const openNewConversationModal = async () => {
    setShowNewModal(true);
    try {
      const { data } = await axios.get('/messages/eligible-practitioners');
      setEligiblePractitioners(Array.isArray(data.practitioners) ? data.practitioners : []);
    } catch (error) {
      toast.error('Could not load your therapists/graphologists');
    }
  };

  const startConversation = async (practitioner) => {
    setStartingConversation(true);
    try {
      const { data } = await axios.post('/messages/conversations', {
        practitionerId: practitioner.id,
        practitionerRole: practitioner.role
      });
      setShowNewModal(false);
      await fetchConversations();
      openConversation({ id: data.conversationId, unreadCount: 0 });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not start conversation');
    } finally {
      setStartingConversation(false);
    }
  };

  const selectedConversation = conversations.find(c => c.id === selectedId);

  return (
    <div style={styles.page}>
      <ToastContainer position="top-center" />

      <div style={styles.list}>
        <div style={styles.listHeader}>
          <h2 style={styles.title}>Messages</h2>
          {isClient && (
            <button onClick={openNewConversationModal} style={styles.newBtn}>+ New</button>
          )}
        </div>

        {loadingConversations ? (
          <p style={styles.muted}>Loading…</p>
        ) : conversations.length === 0 ? (
          <div style={styles.empty}>
            <p style={styles.muted}>No conversations yet.</p>
            {isClient && (
              <button onClick={openNewConversationModal} style={styles.primaryBtn}>
                Message someone you've booked with
              </button>
            )}
          </div>
        ) : (
          conversations.map(c => (
            <div
              key={c.id}
              onClick={() => openConversation(c)}
              style={{
                ...styles.convoRow,
                background: selectedId === c.id ? 'rgba(48,60,108,0.06)' : 'transparent'
              }}
            >
              <img
                src={c.otherImage ? `${BACKEND_URL}/uploads/${c.otherImage}` : '/img/6971412.png'}
                alt={c.otherName}
                onError={(e) => { e.target.src = '/img/6971412.png'; }}
                style={styles.avatar}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.convoTopRow}>
                  <span style={styles.convoName}>{c.otherName || 'Deleted User'}</span>
                  {c.unreadCount > 0 && <span style={styles.unreadBadge}>{c.unreadCount}</span>}
                </div>
                <p style={styles.convoPreview}>{c.lastMessage || 'No messages yet'}</p>
              </div>
            </div>
          ))
        )}
      </div>

      <div style={styles.thread}>
        {!selectedId ? (
          <div style={styles.threadEmpty}>
            <p style={styles.muted}>Select a conversation to view messages.</p>
          </div>
        ) : (
          <>
            <div style={styles.threadHeader}>
              <strong>{selectedConversation?.otherName || 'Deleted User'}</strong>
            </div>

            <div style={styles.threadMessages}>
              {loadingMessages ? (
                <p style={styles.muted}>Loading…</p>
              ) : (
                messages.map(m => {
                  const isMine = m.sender_id === user.id && m.sender_role === user.role;
                  return (
                    <div key={m.id} style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
                      <div style={{ ...styles.bubble, ...(isMine ? styles.bubbleMine : styles.bubbleTheirs) }}>
                        <p style={{ margin: 0 }}>{m.message_text}</p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {selectedConversation && selectedConversation.canMessage === false ? (
              <div style={styles.closedNotice}>
                {isClient
                  ? 'This chat is closed — messaging ends 24 hours after a session. Book another session to continue chatting.'
                  : 'This chat is closed. The client can reopen it by booking another session.'}
                {isClient && (
                  <a href="/doctors" style={styles.closedLink}>Book a session</a>
                )}
              </div>
            ) : (
              <>
                {selectedConversation?.closesAt && (
                  <p style={styles.windowNote}>
                    Chat open until {new Date(selectedConversation.closesAt).toLocaleString()}
                  </p>
                )}
                <form onSubmit={handleSend} style={styles.inputRow}>
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Type a message…"
                    style={styles.input}
                    disabled={sending}
                  />
                  <button type="submit" disabled={sending || !text.trim()} style={styles.sendBtn}>
                    {sending ? '…' : 'Send'}
                  </button>
                </form>
              </>
            )}
          </>
        )}
      </div>

      {showNewModal && (
        <div style={styles.overlay} onClick={() => setShowNewModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Start a conversation</h3>
            {eligiblePractitioners.length === 0 ? (
              <p style={styles.muted}>
                You can message a therapist or graphologist once you've booked a session with them, and for 24 hours after it ends.
              </p>
            ) : (
              eligiblePractitioners.map(p => (
                <button
                  key={`${p.role}-${p.id}`}
                  onClick={() => startConversation(p)}
                  disabled={startingConversation}
                  style={styles.pickerRow}
                >
                  <img
                    src={p.image ? `${BACKEND_URL}/uploads/${p.image}` : '/img/6971412.png'}
                    alt={p.name}
                    onError={(e) => { e.target.src = '/img/6971412.png'; }}
                    style={styles.avatar}
                  />
                  <span>{p.name}</span>
                  <span style={styles.roleTag}>{p.role === 'doctor' ? 'Therapist' : 'Graphologist'}</span>
                </button>
              ))
            )}
            <button onClick={() => setShowNewModal(false)} style={{ ...styles.primaryBtn, marginTop: 16 }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: { display: 'flex', height: 'calc(100vh - 80px)', maxWidth: 1100, margin: '0 auto', border: '1px solid rgba(48,60,108,0.08)' },
  list: { width: 320, flexShrink: 0, borderRight: '1px solid rgba(48,60,108,0.08)', overflowY: 'auto', display: 'flex', flexDirection: 'column' },
  listHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 16px 8px' },
  title: { fontFamily: "'DM Serif Display', serif", margin: 0, color: '#1a2344', fontSize: 20 },
  newBtn: { border: 'none', background: '#1a2344', color: 'white', padding: '6px 12px', borderRadius: 50, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  muted: { color: '#6b7280', fontSize: 14, padding: '0 16px' },
  empty: { padding: 16, textAlign: 'center' },
  primaryBtn: { border: 'none', background: '#1a2344', color: 'white', padding: '10px 18px', borderRadius: 50, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  convoRow: { display: 'flex', gap: 10, padding: '12px 16px', cursor: 'pointer', alignItems: 'center' },
  avatar: { width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
  convoTopRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  convoName: { fontWeight: 600, fontSize: 14, color: '#1a2344', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  convoPreview: { margin: '2px 0 0', fontSize: 13, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  unreadBadge: { background: '#e8622a', color: 'white', fontSize: 11, fontWeight: 700, borderRadius: 50, padding: '1px 7px', flexShrink: 0 },
  thread: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  threadEmpty: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  threadHeader: { padding: 16, borderBottom: '1px solid rgba(48,60,108,0.08)' },
  threadMessages: { flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 8 },
  bubble: { maxWidth: '70%', padding: '10px 14px', borderRadius: 14, fontSize: 14 },
  bubbleMine: { background: '#1a2344', color: 'white' },
  bubbleTheirs: { background: '#f3f4f6', color: '#111827' },
  closedNotice: { padding: 16, borderTop: '1px solid rgba(48,60,108,0.08)', background: '#fafbfc', color: '#6b7280', fontSize: 14, display: 'flex', flexDirection: 'column', gap: 8 },
  closedLink: { color: '#1a2344', fontWeight: 600 },
  windowNote: { margin: 0, padding: '6px 16px 0', fontSize: 12, color: '#9ca3af' },
  inputRow: { display: 'flex', gap: 8, padding: 12, borderTop: '1px solid rgba(48,60,108,0.08)' },
  input: { flex: 1, padding: '10px 14px', borderRadius: 50, border: '1px solid rgba(48,60,108,0.15)', fontSize: 14, outline: 'none' },
  sendBtn: { border: 'none', background: '#1a2344', color: 'white', padding: '10px 20px', borderRadius: 50, fontWeight: 600, cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(26,35,68,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 },
  modal: { background: 'white', borderRadius: 20, padding: 28, maxWidth: 420, width: '100%', maxHeight: '80vh', overflowY: 'auto' },
  pickerRow: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: 10, border: '1px solid rgba(48,60,108,0.1)', borderRadius: 12, background: 'white', cursor: 'pointer', marginBottom: 8, textAlign: 'left' },
  roleTag: { marginLeft: 'auto', fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 700 }
};

export default Messages;